import * as React from "react";
import { useTranslation } from "react-i18next";

function pagerWindow(cur: number, totalPages: number): number[] {
  const set = new Set([1, totalPages, cur - 1, cur, cur + 1]);
  return Array.from(set)
    .filter((n) => n >= 1 && n <= totalPages)
    .sort((a, b) => a - b);
}

export function Pagination({
  page,
  totalPages,
  onGo,
  variant = "numbers",
}: {
  page: number;
  totalPages: number;
  onGo: (p: number) => void;
  variant?: "numbers" | "dots";
}) {
  const { t } = useTranslation("ui");

  if (variant === "dots") {
    return (
      <nav className="flex items-center justify-center gap-1" aria-label={t("pagination")}>
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onGo(n)}
            aria-label={t("pageN", { n })}
            aria-current={n === page ? "page" : undefined}
            className="inline-flex h-11 w-7 items-center justify-center rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            <span
              className={[
                "block h-2 w-2 rounded-full transition-colors duration-200",
                n === page ? "bg-primary" : "bg-primary/25",
              ].join(" ")}
            />
          </button>
        ))}
      </nav>
    );
  }

  const win = pagerWindow(page, totalPages);
  const btn =
    "inline-flex h-10 min-w-10 items-center justify-center border border-primary/15 px-0 text-small font-medium text-primary transition-colors duration-200 [font-variant-numeric:tabular-nums]";
  const out: React.ReactNode[] = [];
  let prev = 0;
  for (const n of win) {
    if (n - prev > 1)
      out.push(
        <span
          key={`gap-${n}`}
          aria-hidden="true"
          className="w-7 select-none text-center text-small text-primary/30"
        >
          …
        </span>,
      );
    out.push(
      <button
        key={n}
        type="button"
        onClick={() => onGo(n)}
        aria-label={t("pageN", { n })}
        aria-current={n === page ? "page" : undefined}
        className={[
          btn,
          n === page
            ? "border-primary bg-primary font-semibold text-white"
            : "hover:border-primary/50",
        ].join(" ")}
      >
        {n}
      </button>,
    );
    prev = n;
  }
  return (
    <nav
      className="mt-12 flex flex-col items-center gap-6 border-t border-primary/15 pt-8 sm:flex-row sm:justify-end"
      aria-label={t("pagination")}
    >
      <div className="inline-flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => onGo(Math.max(1, page - 1))}
          disabled={page === 1}
          aria-label={t("previousPage")}
          className={`${btn} gap-2 px-4 text-micro font-semibold uppercase tracking-eyebrow disabled:cursor-not-allowed disabled:opacity-40`}
        >
          {t("prev")}
        </button>
        {out}
        <button
          type="button"
          onClick={() => onGo(Math.min(totalPages, page + 1))}
          disabled={page === totalPages}
          aria-label={t("nextPage")}
          className={`${btn} gap-2 px-4 text-micro font-semibold uppercase tracking-eyebrow disabled:cursor-not-allowed disabled:opacity-40`}
        >
          {t("next")}
        </button>
      </div>
    </nav>
  );
}
